import { initializeApp } from 'firebase/app'
import {
  getAuth,
  User,
  Auth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  onAuthStateChanged,
  getIdToken,
  getIdTokenResult,
  signOut,
  verifyPasswordResetCode,
  confirmPasswordReset,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  checkActionCode,
  connectAuthEmulator,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  Persistence,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  MultiFactorResolver,
  getMultiFactorResolver,
  MultiFactorInfo,
  MultiFactorSession,
  PhoneInfoOptions,
  MultiFactorError,
  PhoneMultiFactorInfo,
} from 'firebase/auth'
import { FirebaseError } from '@firebase/util'
import { TFirebaseAuthPersistance } from '../models/types'
import { logger } from '../utils/helpers/sentry-logger'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGE_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  tenantId: import.meta.env.VITE_FIREBASE_TENANT_ID,
}

const envPersistance = import.meta.env
  .VITE_FIREBASE_AUTH_PERSISTANCE as TFirebaseAuthPersistance

let persistance: Persistence

if (envPersistance === 'LOCAL_INDEX_DB') {
  persistance = indexedDBLocalPersistence
} else if (envPersistance === 'LOCAL') {
  persistance = browserLocalPersistence
} else if (envPersistance === 'SESSION') {
  persistance = browserSessionPersistence
} else if (envPersistance === 'NONE') {
  persistance = inMemoryPersistence
}

export class Firebase {
  private auth: Auth

  constructor() {
    const app = initializeApp(firebaseConfig)
    this.auth = getAuth(app)
    this.auth.tenantId = firebaseConfig.tenantId || null
    const emulatorUrl = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL as
      | string
      | undefined

    if (emulatorUrl) {
      connectAuthEmulator(this.auth, emulatorUrl, { disableWarnings: true })
    }

    if (persistance) {
      setPersistence(this.auth, persistance)
    }
  }

  // Helper function to safely get phone number from MultiFactorInfo
  private static getPhoneNumberFromHint(hint: MultiFactorInfo): string | null {
    if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      const phoneHint = hint as PhoneMultiFactorInfo
      return phoneHint.phoneNumber || null
    }
    return null
  }

  // Helper function to mask phone number for logging
  private static maskPhoneNumber(phoneNumber: string | null): string {
    if (!phoneNumber) return 'No phone number'
    return `${phoneNumber.slice(0, 3)}***${phoneNumber.slice(-2)}`
  }

  getInstance() {
    return this.auth
  }

  async login({ email, password }: { email: string; password: string }) {
    const userCredential = await signInWithEmailAndPassword(
      this.auth,
      email,
      password
    )
    /* if (!userCredential.user.emailVerified) {
      await sendEmailVerification(userCredential.user)
    } */
    return userCredential
  }

  async reauthenticateWithCredential({
    currentPassword,
  }: {
    currentPassword: string
  }) {
    const currentUser = this.getCurrentUser()
    if (currentUser?.email) {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      )
      return reauthenticateWithCredential(currentUser, credential)
    }
  }

  /**
   * Checks a password reset code sent to the user by email or other out-of-band mechanism.
   *
   * @returns the user's email address if valid.
   *
   * @param actionCode - A verification code sent to the user.
   *
   */
  async verifyPasswordResetCode({ actionCode }: { actionCode: string }) {
    return verifyPasswordResetCode(this.auth, actionCode)
  }

  /**
   * Completes the password reset process, given a confirmation code and new password.
   *
   * @param actionCode - A confirmation code sent to the user.
   * @param newPassword - The new password.
   *
   */
  async confirmPasswordReset({
    actionCode,
    newPassword,
  }: {
    actionCode: string
    newPassword: string
  }) {
    return confirmPasswordReset(this.auth, actionCode, newPassword)
  }

  async updatePassword({ newPassword }: { newPassword: string }) {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      return updatePassword(currentUser, newPassword)
    }

    throw new Error('Could not update password, because could not find user')
  }

  /* async register(name: string, email: string, password: string) {
    await this.auth.createUserWithEmailAndPassword(email, password)
    await this.auth.currentUser?.updateProfile({
      displayName: name,
    })
    return this.auth.currentUser
  } */

  getCurrentUser() {
    return this.auth.currentUser
  }

  onAuthStateChanged(cb: (user: User | null) => void) {
    return onAuthStateChanged(this.auth, cb)
  }

  async loginCustomToken(customToken: string) {
    return signInWithCustomToken(this.auth, customToken)
  }

  async getIdToken(forceRefresh: boolean = false) {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      return getIdToken(currentUser, forceRefresh)
    }

    throw new Error('Could not get id token, because could not find user')
  }

  async getIdTokenResult(forceRefresh: boolean = true) {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const idTokenResult = await getIdTokenResult(currentUser, forceRefresh)
      return idTokenResult
    }
    throw new Error(
      'Could not get id token result, because could not find user'
    )
  }

  async checkActionCode({ actionCode }: { actionCode: string }) {
    return checkActionCode(this.auth, actionCode)
  }

  async logout() {
    return signOut(this.auth)
  }

  // ####################################################
  // ###########             MFA              ###########
  // ####################################################
  // Used in multi-factor enrollment.
  public verificationId: string | null = null

  startEnrollMultiFactor = async (
    phoneNumber: string | null,
    recaptchaId: string = 'recaptcha'
  ) => {
    logger.addBreadcrumb('MFA: Starting MFA enrollment process', {
      phoneNumber: phoneNumber ? Firebase.maskPhoneNumber(phoneNumber) : null,
      recaptchaId,
      currentUser: this.auth.currentUser?.uid || 'No user',
      currentUserEmail: this.auth.currentUser?.email || 'No email',
      timestamp: new Date().toISOString(),
    })

    try {
      /**
       * Pass the input id if setting invisible recaptcha,
       * otherwise pass the container id where recaptcha will
       * be launch.
       */
      logger.addBreadcrumb('MFA: Creating RecaptchaVerifier', {
        recaptchaId,
        size: 'invisible',
      })

      const recaptchaVerifier = new RecaptchaVerifier(this.auth, recaptchaId, {
        size: 'invisible',
      })

      logger.addBreadcrumb('MFA: RecaptchaVerifier created successfully')

      if (this.auth.currentUser) {
        logger.addBreadcrumb(
          'MFA: Current user found, proceeding with MFA session'
        )

        try {
          const multiFactorSession = await multiFactor(
            this.auth.currentUser
          ).getSession()

          logger.addBreadcrumb('MFA: MFA session obtained successfully', {
            sessionType: multiFactorSession,
          })

          // Specify the phone number and pass the MFA session.
          const phoneInfoOptions = {
            phoneNumber,
            session: multiFactorSession,
          }

          logger.addBreadcrumb('MFA: Creating phone info options', {
            phoneNumber: phoneNumber
              ? Firebase.maskPhoneNumber(phoneNumber)
              : null,
            sessionExists: !!multiFactorSession,
          })

          const phoneAuthProvider = new PhoneAuthProvider(this.auth)
          logger.addBreadcrumb(
            'MFA: PhoneAuthProvider created, sending verification SMS'
          )

          // Send SMS verification code.
          this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            recaptchaVerifier
          )

          logger.addBreadcrumb('MFA: SMS verification sent successfully', {
            verificationIdReceived: !!this.verificationId,
            verificationIdLength: this.verificationId?.length || 0,
          })
        } catch (error: unknown) {
          logger.addBreadcrumbWithError(
            'MFA: Error during MFA enrollment process',
            {
              errorCode:
                error instanceof FirebaseError ? error.code : 'unknown',
              errorMessage: error instanceof Error ? error.message : 'unknown',
              phoneNumber: phoneNumber
                ? Firebase.maskPhoneNumber(phoneNumber)
                : null,
              recaptchaId,
              currentUser: this.auth.currentUser?.uid || 'No user',
              stack: error instanceof Error ? error.stack : undefined,
            }
          )

          throw error
        }

        logger.addBreadcrumb('MFA: MFA enrollment process completed', {
          verificationIdSet: !!this.verificationId,
        })
      } else {
        logger.addBreadcrumbWithError(
          'MFA: No current user found for MFA enrollment',
          {
            authState: this.auth.currentUser,
            timestamp: new Date().toISOString(),
          }
        )
      }
    } catch (error: unknown) {
      logger.addBreadcrumbWithError(
        'MFA: Unexpected error in startEnrollMultiFactor',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          phoneNumber: phoneNumber
            ? Firebase.maskPhoneNumber(phoneNumber)
            : null,
          recaptchaId,
          stack: error instanceof Error ? error.stack : undefined,
        }
      )

      throw error
    }
  }

  finishEnrollMultiFactor = async (verificationCode: string) => {
    logger.addBreadcrumb('MFA: Starting MFA enrollment completion', {
      verificationCodeLength: verificationCode?.length || 0,
      verificationIdExists: !!this.verificationId,
      currentUser: this.auth.currentUser?.uid || 'No user',
      currentUserEmail: this.auth.currentUser?.email || 'No email',
      timestamp: new Date().toISOString(),
    })

    try {
      if (this.verificationId && this.auth.currentUser) {
        logger.addBreadcrumb(
          'MFA: Prerequisites met, creating phone credential'
        )

        // User will insert the verification code. Then:
        const cred = PhoneAuthProvider.credential(
          this.verificationId,
          verificationCode
        )

        logger.addBreadcrumb('MFA: Phone credential created successfully')

        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

        logger.addBreadcrumb(
          'MFA: Multi-factor assertion created, proceeding with enrollment'
        )

        // Complete enrollment.
        try {
          await multiFactor(this.auth.currentUser).enroll(
            multiFactorAssertion,
            this.auth.currentUser.email
          )

          logger.addBreadcrumb('MFA: MFA enrollment completed successfully', {
            userEmail: this.auth.currentUser?.email,
            userId: this.auth.currentUser?.uid,
            timestamp: new Date().toISOString(),
          })
        } catch (error: unknown) {
          logger.addBreadcrumbWithError(
            'MFA: Error during MFA enrollment completion',
            {
              errorCode:
                error instanceof FirebaseError ? error.code : 'unknown',
              errorMessage: error instanceof Error ? error.message : 'unknown',
              verificationCodeLength: verificationCode?.length || 0,
              currentUser: this.auth.currentUser?.uid || 'No user',
              currentUserEmail: this.auth.currentUser?.email || 'No email',
              stack: error instanceof Error ? error.stack : undefined,
            }
          )

          throw error
        }

        logger.addBreadcrumb(
          'MFA: Clearing verification ID after successful enrollment'
        )
        this.verificationId = null
      } else {
        logger.addBreadcrumbWithError(
          'MFA: Prerequisites not met for MFA enrollment completion',
          {
            verificationIdExists: !!this.verificationId,
            currentUserExists: !!this.auth.currentUser,
            currentUser: this.auth.currentUser?.uid || 'No user',
            verificationCode: verificationCode
              ? `${verificationCode.slice(0, 2)}***${verificationCode.slice(
                  -1
                )}`
              : 'No code',
          }
        )
      }
    } catch (error: unknown) {
      logger.addBreadcrumbWithError(
        'MFA: Unexpected error in finishEnrollMultiFactor',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          verificationCodeLength: verificationCode?.length || 0,
          verificationIdExists: !!this.verificationId,
          currentUser: this.auth.currentUser?.uid || 'No user',
          stack: error instanceof Error ? error.stack : undefined,
        }
      )

      throw error
    }
  }

  public multiFactorResolver: MultiFactorResolver | null = null

  getMfaResolver = (error: MultiFactorError) => {
    logger.addBreadcrumb('MFA: Creating MFA resolver from error', {
      errorCode: error.code || 'unknown',
      errorMessage: error.message || 'unknown',
      customData: error.customData || 'No custom data',
      timestamp: new Date().toISOString(),
    })

    try {
      this.multiFactorResolver = getMultiFactorResolver(this.auth, error)

      logger.addBreadcrumb('MFA: MFA resolver created successfully', {
        resolverExists: !!this.multiFactorResolver,
        hintsCount: this.multiFactorResolver?.hints?.length || 0,
        sessionExists: !!this.multiFactorResolver?.session,
        timestamp: new Date().toISOString(),
      })

      // Log details about available hints for debugging
      if (this.multiFactorResolver?.hints) {
        this.multiFactorResolver.hints.forEach((hint, index) => {
          logger.addBreadcrumb(`MFA: MFA Hint ${index}:`, {
            factorId: hint.factorId,
            uid: hint.uid,
            displayName: hint.displayName || 'No display name',
            hint,
          })
        })
      }

      return this.multiFactorResolver
    } catch (resolverError: unknown) {
      logger.addBreadcrumbWithError('MFA: Error creating MFA resolver', {
        resolverError:
          resolverError instanceof Error
            ? resolverError.message
            : 'Unknown resolver error',
        originalErrorCode:
          error instanceof FirebaseError ? error.code : 'unknown',
        originalErrorMessage:
          error instanceof Error ? error.message : 'unknown',
        stack: resolverError instanceof Error ? resolverError.stack : undefined,
      })

      throw resolverError
    }
  }

  startMfaSignin = async (
    multiFactorHint: MultiFactorInfo,
    session: MultiFactorSession,
    recaptchaId: string = 'recaptcha'
  ) => {
    logger.addBreadcrumb('MFA: Starting MFA sign-in process', {
      factorId: multiFactorHint.factorId,
      factorDisplayName: multiFactorHint.displayName || 'No display name',
      phoneNumber: Firebase.maskPhoneNumber(
        Firebase.getPhoneNumberFromHint(multiFactorHint)
      ),
      factorUid: multiFactorHint.uid,
      sessionType: 'MultiFactorSession',
      recaptchaId,
      timestamp: new Date().toISOString(),
    })

    try {
      logger.addBreadcrumb('MFA: Creating RecaptchaVerifier for MFA sign-in', {
        recaptchaId,
        size: 'invisible',
      })

      const recaptchaVerifier = new RecaptchaVerifier(this.auth, recaptchaId, {
        size: 'invisible',
      })

      logger.addBreadcrumb('MFA: RecaptchaVerifier created for MFA sign-in')

      if (multiFactorHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        logger.addBreadcrumb(
          'MFA: Phone factor detected, proceeding with phone verification'
        )

        const phoneInfoOptions: PhoneInfoOptions = {
          multiFactorHint,
          session,
        }

        logger.addBreadcrumb(
          'MFA: Creating phone info options for MFA sign-in',
          {
            hintUid: multiFactorHint.uid,
            hintPhoneNumber: Firebase.maskPhoneNumber(
              Firebase.getPhoneNumberFromHint(multiFactorHint)
            ),
            sessionExists: !!session,
          }
        )

        const phoneAuthProvider = new PhoneAuthProvider(this.auth)
        logger.addBreadcrumb(
          'MFA: PhoneAuthProvider created for MFA sign-in, sending verification SMS'
        )

        // Send SMS verification code
        try {
          this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            recaptchaVerifier
          )

          logger.addBreadcrumb(
            'MFA: MFA sign-in SMS verification sent successfully',
            {
              verificationIdReceived: !!this.verificationId,
              verificationIdLength: this.verificationId?.length || 0,
              factorUid: multiFactorHint.uid,
            }
          )
        } catch (error: unknown) {
          logger.addBreadcrumbWithError(
            'MFA: Error during MFA sign-in SMS verification',
            {
              errorCode:
                error instanceof FirebaseError ? error.code : 'unknown',
              errorMessage: error instanceof Error ? error.message : 'unknown',
              factorId: multiFactorHint.factorId,
              factorUid: multiFactorHint.uid,
              phoneNumber: Firebase.maskPhoneNumber(
                Firebase.getPhoneNumberFromHint(multiFactorHint)
              ),
              recaptchaId,
              stack: error instanceof Error ? error.stack : undefined,
            }
          )

          throw error
        }

        logger.addBreadcrumb(
          'MFA: MFA sign-in verification process completed',
          {
            verificationIdSet: !!this.verificationId,
          }
        )
      } else {
        logger.addBreadcrumbWithError(
          'MFA: Unsupported factor type for MFA sign-in',
          {
            factorId: multiFactorHint.factorId,
            expectedFactorId: PhoneMultiFactorGenerator.FACTOR_ID,
            factorUid: multiFactorHint.uid,
          }
        )
        // Should never arrive here.
        // Only phone number second factors are supported.
      }
    } catch (error: unknown) {
      logger.addBreadcrumbWithError('MFA: Unexpected error in startMfaSignin', {
        error: error instanceof Error ? error.message : 'Unknown error',
        factorId: multiFactorHint.factorId,
        factorUid: multiFactorHint.uid,
        phoneNumber: Firebase.maskPhoneNumber(
          Firebase.getPhoneNumberFromHint(multiFactorHint)
        ),
        recaptchaId,
        stack: error instanceof Error ? error.stack : undefined,
      })

      throw error
    }
  }

  finishMfaSignIn = async (verificationCode: string) => {
    logger.addBreadcrumb('MFA: Starting MFA sign-in completion', {
      verificationCodeLength: verificationCode?.length || 0,
      verificationIdExists: !!this.verificationId,
      multiFactorResolverExists: !!this.multiFactorResolver,
      timestamp: new Date().toISOString(),
    })

    try {
      // Get the SMS verification code sent to the user.
      if (this.verificationId && this.multiFactorResolver) {
        logger.addBreadcrumb(
          'MFA: Prerequisites met for MFA sign-in completion'
        )

        const cred = PhoneAuthProvider.credential(
          this.verificationId,
          verificationCode
        )

        logger.addBreadcrumb('MFA: Phone credential created for MFA sign-in')

        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

        logger.addBreadcrumb(
          'MFA: Multi-factor assertion created for MFA sign-in'
        )

        // Complete sign-in.
        try {
          const userCredential = await this.multiFactorResolver.resolveSignIn(
            multiFactorAssertion
          )

          logger.addBreadcrumb('MFA: MFA sign-in completed successfully', {
            userId: userCredential?.user?.uid || 'No user ID',
            userEmail: userCredential?.user?.email || 'No email',
            userPhoneNumber: userCredential?.user?.phoneNumber || 'No phone',
            providerId: userCredential?.providerId || 'No provider',
            timestamp: new Date().toISOString(),
          })
          // We can use userCredential as a prop to get the user info if desired.
          // User successfully signed in with the second factor phone number.
        } catch (error: unknown) {
          logger.addBreadcrumbWithError(
            'MFA: Error during MFA sign-in completion',
            {
              errorCode:
                error instanceof FirebaseError ? error.code : 'unknown',
              errorMessage: error instanceof Error ? error.message : 'unknown',
              verificationCodeLength: verificationCode?.length || 0,
              verificationIdExists: !!this.verificationId,
              multiFactorResolverExists: !!this.multiFactorResolver,
              stack: error instanceof Error ? error.stack : undefined,
            }
          )
          throw error
        }
      } else {
        logger.addBreadcrumbWithError(
          'MFA: Prerequisites not met for MFA sign-in completion',
          {
            verificationIdExists: !!this.verificationId,
            multiFactorResolverExists: !!this.multiFactorResolver,
            verificationCode: verificationCode
              ? `${verificationCode.slice(0, 2)}***${verificationCode.slice(
                  -1
                )}`
              : 'No code',
          }
        )
      }

      logger.addBreadcrumb('MFA: Clearing MFA sign-in state after completion')
      this.multiFactorResolver = null
      this.verificationId = null
    } catch (error: unknown) {
      logger.addBreadcrumbWithError(
        'MFA: Unexpected error in finishMfaSignIn',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          verificationCodeLength: verificationCode?.length || 0,
          verificationIdExists: !!this.verificationId,
          multiFactorResolverExists: !!this.multiFactorResolver,
          stack: error instanceof Error ? error.stack : undefined,
        }
      )

      // Clear state even on error to prevent stuck state
      logger.addBreadcrumb('MFA: Clearing MFA sign-in state after error')
      this.multiFactorResolver = null
      this.verificationId = null

      throw error
    }
  }

  // Not in use, might come in handy later.
  unEnrollMultiFactor = async () => {
    // Check if the user is enrolled in MFA.
    const checkMfaEnrollment = multiFactor(
      this.auth.currentUser!
    ).enrolledFactors
    // Unenroll all the MFA entries in the current user by uid.
    for (let i = 0; i < checkMfaEnrollment.length; i += 1) {
      await multiFactor(this.auth.currentUser!).unenroll(
        checkMfaEnrollment[i].uid
      )
    }
  }

  // ####################################################
  // ###########          END MFA             ###########
  // ####################################################
}

export default new Firebase()
